<script setup lang="ts">
interface Props {
  title: string;
  subtitle?: string;
  selected?: boolean;
  disabled?: boolean;
}

withDefaults(defineProps<Props>(), {
  subtitle: undefined,
  selected: false,
  disabled: false,
});

const emit = defineEmits<{
  (event: 'click'): void;
}>();

function onActivate() {
  emit('click');
}
</script>

<template>
  <div
    class="pj-list-item"
    :class="{
      'pj-list-item--selected': selected,
      'pj-list-item--disabled': disabled,
    }"
    role="button"
    :tabindex="disabled ? -1 : 0"
    :aria-disabled="disabled || undefined"
    :aria-pressed="selected"
    @click="onActivate"
    @keydown.enter.prevent="onActivate"
    @keydown.space.prevent="onActivate"
  >
    <span v-if="$slots.leading" class="pj-list-item__leading">
      <slot name="leading" />
    </span>
    <span class="pj-list-item__content">
      <span class="pj-list-item__title">{{ title }}</span>
      <span v-if="subtitle" class="pj-list-item__subtitle">{{ subtitle }}</span>
    </span>
    <span v-if="$slots.trailing" class="pj-list-item__trailing">
      <slot name="trailing" />
    </span>
  </div>
</template>

<style lang="scss" scoped>
.pj-list-item {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: var(--pj-space-3);
  width: 100%;
  padding: var(--pj-space-4);
  border-radius: var(--pj-radius-xl);
  border: 1px solid var(--pj-color-brand-gray-350, #c6c7bf);
  background: var(--pj-color-brand-white, #ffffff);
  cursor: pointer;
  user-select: none;
  transition:
    border-color var(--pj-motion-duration-fast) var(--pj-motion-easing-standard),
    background-color var(--pj-motion-duration-fast) var(--pj-motion-easing-standard);

  &--selected {
    border-color: var(--pj-color-brand-gray-850, #222926);
    box-shadow: inset 0 0 0 1px var(--pj-color-brand-gray-850, #222926);
  }

  &--disabled {
    cursor: not-allowed;
    background: var(--pj-color-brand-gray-150, #f1f0ee);
    border-color: var(--pj-color-brand-gray-350, #c6c7bf);
  }

  &--disabled .pj-list-item__title,
  &--disabled .pj-list-item__subtitle {
    color: var(--pj-color-brand-gray-550, #74756f);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 var(--pj-border-width-ring) var(--pj-color-focus-ring);
  }

  &--selected:focus-visible {
    box-shadow:
      inset 0 0 0 1px var(--pj-color-brand-gray-850, #222926),
      0 0 0 var(--pj-border-width-ring) var(--pj-color-focus-ring);
  }
}

.pj-list-item__leading {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

.pj-list-item__content {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
  text-align: left;
}

.pj-list-item__title {
  font-family: var(--pj-font-family-sans);
  font-size: var(--pj-font-size-base);
  font-weight: var(--pj-font-weight-medium);
  line-height: 20px;
  letter-spacing: -0.5px;
  color: var(--pj-color-brand-gray-850, #222926);
}

.pj-list-item__subtitle {
  font-family: var(--pj-font-family-sans);
  font-size: var(--pj-font-size-sm);
  font-weight: var(--pj-font-weight-regular);
  line-height: 22px;
  letter-spacing: -0.5px;
  color: var(--pj-color-brand-gray-550, #74756f);
}

.pj-list-item__trailing {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

@media (prefers-reduced-motion: reduce) {
  .pj-list-item {
    transition: none;
  }
}
</style>
